import React, { useContext, useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { getMediaUrl, getUser } from '../services/api';

function Navbar() {
  const { user, logout, unreadCount } = useContext(AuthContext);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  // Fetch current profile image from API
  useEffect(() => {
    if (!user?.id) return;
    getUser(user.id)
      .then((res) => {
        const data = res.data;
        if (data?.profile_image) setProfileImage(data.profile_image);
      })
      .catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setDropdownOpen(false);
        triggerRef.current?.focus();
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const items = dropdownRef.current?.querySelectorAll('[role="menuitem"]');
        if (!items || items.length === 0) return;
        const currentIndex = Array.from(items).indexOf(document.activeElement);
        let nextIndex;
        if (e.key === 'ArrowDown') {
          nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        } else {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        }
        items[nextIndex]?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [dropdownOpen]);

  if (!user) return null;

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const getInitial = () => {
    if (user?.display_name) return user.display_name[0].toUpperCase();
    if (user?.username) return user.username[0].toUpperCase();
    return '?';
  };

  const handleTriggerKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setDropdownOpen(!dropdownOpen);
    }
  };

  return (
    <nav className="navbar">
      <div className="navbar-content">
        <div className="navbar-left">
          <div
            className="navbar-profile"
            ref={triggerRef}
            onClick={() => setDropdownOpen(!dropdownOpen)}
            onKeyDown={handleTriggerKeyDown}
            aria-haspopup="true"
            aria-expanded={dropdownOpen}
            role="button"
            tabIndex={0}
          >
            <div className="navbar-avatar">
              {profileImage ? (
                <img
                  src={getMediaUrl(profileImage)}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                />
              ) : (
                getInitial()
              )}
            </div>
            {dropdownOpen && (
              <div className="navbar-dropdown" role="menu" ref={dropdownRef}>
                <Link
                  to={`/profile/${user.id}`}
                  role="menuitem"
                  tabIndex={-1}
                  onClick={() => setDropdownOpen(false)}
                >
                  View Profile
                </Link>
                <Link
                  to="/showcase"
                  role="menuitem"
                  tabIndex={-1}
                  onClick={() => setDropdownOpen(false)}
                >
                  My Showcase
                </Link>
                <Link
                  to="/saved"
                  role="menuitem"
                  tabIndex={-1}
                  onClick={() => setDropdownOpen(false)}
                >
                  Saved Posts
                </Link>
                <div className="navbar-dropdown-divider" role="separator"></div>
                <Link
                  to="/profile-settings"
                  role="menuitem"
                  tabIndex={-1}
                  onClick={() => setDropdownOpen(false)}
                >
                  Profile Designer
                </Link>
                <button
                  role="menuitem"
                  tabIndex={-1}
                  onClick={() => {
                    logout();
                    setDropdownOpen(false);
                    // SHO-40: send the user to /login explicitly instead of
                    // letting PrivateRoute bounce them to the marketing
                    // Landing page.
                    navigate('/login');
                  }}
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="navbar-center">
          <Link to="/feed" className="navbar-brand">
            Showcase
          </Link>
        </div>
        <div className="navbar-right">
          <Link to="/feed" className={`navbar-link-item ${isActive('/feed') ? 'active' : ''}`}>
            <span className="nav-text-short">Feed</span>
            <span className="nav-text-full">Your Feed.</span>
          </Link>
          <Link
            to="/network"
            className={`navbar-link-item ${isActive('/network') ? 'active' : ''}`}
          >
            <span className="nav-text-short">Network</span>
            <span className="nav-text-full">Your Network.</span>
          </Link>
          <Link to="/jobs" className={`navbar-link-item ${isActive('/jobs') ? 'active' : ''}`}>
            <span className="nav-text-short">Jobs</span>
            <span className="nav-text-full">Your Jobs.</span>
          </Link>
          <Link
            to="/messages"
            className={`navbar-link-item ${isActive('/messages') ? 'active' : ''}`}
          >
            <span className="nav-text-short">Messages</span>
            <span className="nav-text-full">Your Messages.</span>
            {unreadCount > 0 && <span className="navbar-unread-badge">{unreadCount}</span>}
          </Link>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
